import React, { useEffect, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../Firebase/firebase-config"; 
import { useCurrentUid } from "../hooks/useCurrentUid";
import "./BroadcastTicker.css";

const UserBroadcastTicker = () => {
    const [broadcasts, setBroadcasts] = useState([]);
    const currentUserId = useCurrentUid();
    useEffect(() => {
        if (!currentUserId) return;

        const broadcastRef = ref(db, `broadcasts/${currentUserId}`);
        
        const unsubscribe = onValue(broadcastRef, (snapshot) => {
            const data = snapshot.val();
            
            if (data) {
                const activeBroadcasts = Object.values(data)
                    .filter((item) => item && item.status === true) // ✅ STRICT TRUE ONLY
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                setBroadcasts(activeBroadcasts);
            } else {
                setBroadcasts([]);
            }
        });

        return () => unsubscribe();
    }, [currentUserId]);

    if (broadcasts.length === 0) return null;

    return (
        <div className="broadcast-ticker-wrapper">
            {broadcasts.length === 1 ? (
                <SingleBroadcast broadcast={broadcasts[0]} />
            ) : (
                <BroadcastCarousel broadcasts={broadcasts} />
            )}
        </div>
    );
};


// ✅ SINGLE
const SingleBroadcast = ({ broadcast }) => {
    const imageUrl = broadcast?.imageUrl?.trim() || broadcast?.imgUrl?.trim() || "";
    const [imgError, setImgError] = useState(false);
    

    const hasImage = !!imageUrl && !imgError;
    
    return (
        <div className="broadcast-ticker-item">
            <div className="broadcast-content">

                {hasImage ? (
                    <div className="broadcast-image-container">
                        <img 
                            src={imageUrl}
                            alt="Broadcast"
                            className="broadcast-image"
                            onError={() => setImgError(true)} // ✅ fallback trigger
                        />
                    </div>
                ) : (
                    <div className="broadcast-text">
                        <div className="broadcast-header">
                            <span className="broadcast-icon">📢</span>
                            <strong>{broadcast.heading}</strong>
                        </div>
                        <p className="broadcast-message">{broadcast.message}</p>
                        {broadcast.validity && (
                            <div className="broadcast-validity">
                                <strong>Validity:</strong> {broadcast.validity}
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};


// ✅ CAROUSEL
const BroadcastCarousel = ({ broadcasts }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        if (broadcasts.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % broadcasts.length);
            setImgError(false); // reset error when slide changes
        }, 5000);

        return () => clearInterval(interval);
    }, [broadcasts.length]);

    const current = broadcasts[currentIndex];
    const imageUrl = current?.imageUrl?.trim() || current?.imgUrl?.trim() || "";
    const hasImage = !!imageUrl && !imgError;

    return (
        <div className="broadcast-carousel">
            <div className="broadcast-ticker-item">
                <div className="broadcast-content">

                    {hasImage ? (
                        <div className="broadcast-image-container">
                            <img 
                                src={imageUrl}
                                alt="Broadcast"
                                className="broadcast-image"
                                onError={() => setImgError(true)}
                            />
                        </div>
                    ) : (
                        <div className="broadcast-text">
                            <div className="broadcast-header">
                                <span className="broadcast-icon">📢</span>
                                <strong>{current.heading}</strong>
                            </div>
                            <p className="broadcast-message">{current.message}</p>
                            {current.validity && (
                                <div className="broadcast-validity">
                                    <strong>Validity:</strong> {current.validity}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* Dots */}
            {broadcasts.length > 1 && (
                <div className="carousel-dots">
                    {broadcasts.map((_, index) => (
                        <button
                            key={index}
                            className={`dot ${index === currentIndex ? "active" : ""}`}
                            onClick={() => {
                                setCurrentIndex(index);
                                setImgError(false);
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default UserBroadcastTicker;